/* ═══════════════════════════════════════════════════════
   PFI TB-10 — Factored Diameter Inches (FDI) Data
   ═══════════════════════════════════════════════════════ */

// Table D — Wall Thickness Factor
export const wallFactorRanges: { max: number; factor: number }[] = [
  { max: 0.375, factor: 1.0 },
  { max: 0.500, factor: 1.2 },
  { max: 0.625, factor: 1.4 },
  { max: 0.750, factor: 1.6 },
  { max: 0.875, factor: 1.8 },
  { max: 1.000, factor: 2.0 },
  { max: 1.125, factor: 2.2 },
  { max: 1.250, factor: 2.4 },
  { max: 1.375, factor: 2.6 },
  { max: 1.500, factor: 2.8 },
  { max: 1.625, factor: 3.0 },
  { max: 1.750, factor: 3.2 },
  { max: 1.875, factor: 3.4 },
  { max: 2.000, factor: 3.6 },
  { max: 2.125, factor: 3.8 },
  { max: 2.250, factor: 4.0 },
  { max: 2.375, factor: 4.2 },
  { max: 3.500, factor: 4.4 },
];

export const getWallFactor = (wall: number): number => {
  for (const r of wallFactorRanges) {
    if (wall <= r.max) return r.factor;
  }
  return 4.4; // max
};

// Table A — Material Factor (by ASME P-Number)
export interface MaterialOption {
  label: string;
  pNumber: string;
  factor: number;
}

export const materialOptions: MaterialOption[] = [
  { label: "Carbon Steel (P1)", pNumber: "P1", factor: 1.0 },
  { label: "½Mo / ½Cr-½Mo (P3)", pNumber: "P3", factor: 1.5 },
  { label: "1¼Cr-½Mo (P4)", pNumber: "P4", factor: 1.5 },
  { label: "2¼Cr-1Mo (P5A)", pNumber: "P5A", factor: 1.5 },
  { label: "5Cr-½Mo / 9Cr-1Mo (P5B)", pNumber: "P5B", factor: 1.5 },
  { label: "Cr-Mo-V (P5C)", pNumber: "P5C", factor: 1.5 },
  { label: "Martensitic SS (P6)", pNumber: "P6", factor: 1.5 },
  { label: "Ferritic SS (P7)", pNumber: "P7", factor: 2.0 },
  { label: "Austenitic SS 304/316/347 (P8)", pNumber: "P8", factor: 2.0 },
  { label: "2–4 Nickel Steels (P9)", pNumber: "P9", factor: 2.5 },
  { label: "Various Low Alloy (P10)", pNumber: "P10", factor: 2.5 },
  { label: "Duplex / Super Duplex SS (P10H)", pNumber: "P10H", factor: 2.5 },
  { label: "High Strength Low Alloy (P11)", pNumber: "P11", factor: 2.5 },
  { label: "High Cr-Mo Alloy (P15)", pNumber: "P15", factor: 2.5 },
];

// Table E — Weld Type Factor
export type WeldTypeId =
  | "butt"
  | "socket_small"
  | "socket_large"
  | "irbc_3000"
  | "irbc_6000"
  | "irbc_9000"
  | "irbc_bw_small"
  | "irbc_bw_large"
  | "nozzle_stub_on"
  | "nozzle_stub_in"
  | "linear";

export interface WeldTypeOption {
  id: WeldTypeId;
  label: string;
  description: string;
  /** true = uses branch NPS in calculation instead of main NPS */
  usesBranch: boolean;
  /** true = uses header wall factor in calculation */
  usesHeaderWF: boolean;
  /** true = uses nozzle wall factor */
  usesNozzleWF: boolean;
  /** base multiplier applied to branch size */
  branchMultiplier: number;
  /** simple factor when usesBranch is false */
  factor: number;
}

export const weldTypeOptions: WeldTypeOption[] = [
  { id: "butt", label: "Butt Weld", description: "All sizes", usesBranch: false, usesHeaderWF: false, usesNozzleWF: false, branchMultiplier: 0, factor: 1.0 },
  { id: "socket_small", label: "Socket Weld (≤2\")", description: "2\" and below", usesBranch: false, usesHeaderWF: false, usesNozzleWF: false, branchMultiplier: 0, factor: 1.25 },
  { id: "socket_large", label: "Socket Weld (>2\")", description: "Over 2\"", usesBranch: false, usesHeaderWF: false, usesNozzleWF: false, branchMultiplier: 0, factor: 0.75 },
  { id: "irbc_3000", label: "IRBC 3000#", description: "Branch × 2.0", usesBranch: true, usesHeaderWF: false, usesNozzleWF: false, branchMultiplier: 2.0, factor: 0 },
  { id: "irbc_6000", label: "IRBC 6000#", description: "Branch × 3.0", usesBranch: true, usesHeaderWF: false, usesNozzleWF: false, branchMultiplier: 3.0, factor: 0 },
  { id: "irbc_9000", label: "IRBC 9000#", description: "Branch × 4.0", usesBranch: true, usesHeaderWF: false, usesNozzleWF: false, branchMultiplier: 4.0, factor: 0 },
  { id: "irbc_bw_small", label: "IRBC Buttweld (≤2\")", description: "Branch × 3.0 × WF(header)", usesBranch: true, usesHeaderWF: true, usesNozzleWF: false, branchMultiplier: 3.0, factor: 0 },
  { id: "irbc_bw_large", label: "IRBC Buttweld (>2\")", description: "Branch × 4.0 × WF(header)", usesBranch: true, usesHeaderWF: true, usesNozzleWF: false, branchMultiplier: 4.0, factor: 0 },
  { id: "nozzle_stub_on", label: "Nozzle 90° Stub-On", description: "Branch × 2.0 × WF(nozzle)", usesBranch: true, usesHeaderWF: false, usesNozzleWF: true, branchMultiplier: 2.0, factor: 0 },
  { id: "nozzle_stub_in", label: "Nozzle 90° Stub-In", description: "Branch × 3.0 × WF(header)", usesBranch: true, usesHeaderWF: true, usesNozzleWF: false, branchMultiplier: 3.0, factor: 0 },
  { id: "linear", label: "Linear Weld", description: "Lin.in × π × MF × WF", usesBranch: false, usesHeaderWF: false, usesNozzleWF: false, branchMultiplier: 0, factor: 0 },
];

// Common NPS pipe sizes for dropdown
export const npsSizes = [
  0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3, 3.5, 4, 5, 6, 8, 10, 12,
  14, 16, 18, 20, 22, 24, 26, 28, 30, 32, 34, 36, 38, 40, 42, 44, 46, 48,
];

export const npsLabel = (n: number): string => {
  if (n === 0.5) return '½"';
  if (n === 0.75) return '¾"';
  if (n === 1.25) return '1¼"';
  if (n === 1.5) return '1½"';
  if (n === 2.5) return '2½"';
  if (n === 3.5) return '3½"';
  return `${n}"`;
};

// Reference tables for display
export const wallFactorHeaders = ["Wall Thickness", "Factor"];
export const wallFactorRows: (string | number)[][] = [
  ['≤ 0.375"', 1.0],
  ['0.376" – 0.500"', 1.2],
  ['0.501" – 0.625"', 1.4],
  ['0.626" – 0.750"', 1.6],
  ['0.751" – 0.875"', 1.8],
  ['0.876" – 1.000"', 2.0],
  ['1.001" – 1.125"', 2.2],
  ['1.126" – 1.250"', 2.4],
  ['1.251" – 1.375"', 2.6],
  ['1.376" – 1.500"', 2.8],
  ['1.501" – 1.625"', 3.0],
  ['1.626" – 1.750"', 3.2],
  ['1.751" – 1.875"', 3.4],
  ['1.876" – 2.000"', 3.6],
  ['2.001" – 2.125"', 3.8],
  ['2.126" – 2.250"', 4.0],
  ['2.251" – 2.375"', 4.2],
  ['2.376" – 3.500"', 4.4],
];

export const materialFactorHeaders = ["P-Number", "Material", "Factor"];
export const materialFactorRows: (string | number)[][] = materialOptions.map(m => [m.pNumber, m.label, m.factor]);

export const weldTypeHeaders = ["Weld Type", "Size Range", "Factor"];
export const weldTypeRows: (string | number)[][] = [
  ["Butt Weld", "All", "1.00"],
  ["Socket Weld", "≤ 2\"", "1.25"],
  ["Socket Weld", "> 2\"", "0.75"],
  ["IRBC", "3000#", "Branch × 2.0"],
  ["IRBC", "6000#", "Branch × 3.0"],
  ["IRBC", "9000#", "Branch × 4.0"],
  ["IRBC Buttweld", "≤ 2\"", "Branch × 3.0 × WF(hdr)"],
  ["IRBC Buttweld", "> 2\"", "Branch × 4.0 × WF(hdr)"],
  ["Nozzle 90° Stub-On", "All", "Branch × 2.0 × WF(nozzle)"],
  ["Nozzle 90° Stub-In", "All", "Branch × 3.0 × WF(hdr)"],
  ["Linear Weld", "All", "Lin.in × π × MF × WF"],
];
